import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { devicesApi, getApiErrorMessage, queryKeys, deviceQueries } from '@milliswift/api-client'
import {
  Accordion,
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  PageSkeleton,
  useToast,
  type Column,
} from '@milliswift/ui'
import { Check, Monitor, ShieldAlert, X } from 'lucide-react'
import { useState } from 'react'
import type { Device, PairingRequest } from '@milliswift/types'

/**
 * Device management: pending approvals front and centre, active devices, and
 * a collapsed history of revoked ones.
 *
 * A pending request must be approved with a human check — the verification
 * code shown here has to match the one the agent printed, because that is the
 * only thing proving the request came from your machine and not someone
 * replaying a key.
 */
export default function Devices() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const devices = useQuery(deviceQueries.list())

  const [approveTarget, setApproveTarget] = useState<PairingRequest | null>(null)
  const [nickname, setNickname] = useState('')
  const [revokeTarget, setRevokeTarget] = useState<Device | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.devices })

  const approve = useMutation({
    mutationFn: (pairing: PairingRequest) =>
      devicesApi.approve(pairing.pairing_id, {
        nickname: nickname.trim() || undefined,
      }),
    onSuccess: () => {
      setApproveTarget(null)
      setNickname('')
      toast.success('Device approved')
      void invalidate()
    },
    onError: (error) => toast.error('Could not approve', getApiErrorMessage(error)),
  })

  const reject = useMutation({
    mutationFn: (pairingId: string) => devicesApi.reject(pairingId),
    onSuccess: () => {
      toast.success('Request rejected')
      void invalidate()
    },
    onError: (error) => toast.error('Could not reject', getApiErrorMessage(error)),
  })

  const revoke = useMutation({
    mutationFn: (deviceId: string) => devicesApi.revoke(deviceId),
    onSuccess: () => {
      setRevokeTarget(null)
      toast.success('Device revoked')
      void invalidate()
    },
    onError: (error) => toast.error('Could not revoke', getApiErrorMessage(error)),
  })

  if (devices.isPending) return <PageSkeleton />

  const pending = devices.data?.pending ?? []
  const active = devices.data?.active ?? []
  const revoked = devices.data?.revoked ?? []

  const activeColumns: Column<Device>[] = [
    {
      key: 'name',
      header: 'Device',
      sortValue: (d) => d.nickname ?? d.device_hostname ?? d.device_id,
      cell: (d) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">
            {d.nickname ?? d.device_hostname ?? 'Unnamed device'}
          </p>
          <p className="truncate text-caption text-muted">{d.device_os ?? 'unknown OS'}</p>
        </div>
      ),
    },
    {
      key: 'ip',
      header: 'Network',
      sortValue: (d) => d.device_ip ?? '',
      cell: (d) => (
        <div>
          <p className="font-mono text-caption text-foreground">{d.device_ip ?? '—'}</p>
          <p className="text-caption text-muted">{d.device_region ?? ''}</p>
        </div>
      ),
    },
    {
      key: 'last_seen',
      header: 'Last seen',
      sortValue: (d) => d.last_seen_at ?? '',
      cell: (d) => (d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : 'never'),
    },
    {
      key: 'session',
      header: 'Session',
      cell: (d) =>
        d.session_expires_at > new Date().toISOString() ? (
          <Badge tone="success" dot>
            Active
          </Badge>
        ) : (
          <Badge tone="warning" dot>
            Expired
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (d) => (
        <Button size="sm" variant="ghost" onClick={() => setRevokeTarget(d)}>
          Revoke
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Devices</h1>
        <p className="mt-1 text-sm text-muted">
          Every device must be approved before it can query. Approvals are checked against a
          code both ends can see.
        </p>
      </div>

      {/* ---------------- pending ---------------- */}
      {pending.length > 0 ? (
        <section className="space-y-3" aria-label="Pending approvals">
          <h2 className="text-caption font-semibold uppercase tracking-widest text-danger-text">
            Awaiting your approval ({pending.length})
          </h2>

          <ul className="space-y-3">
            {pending.map((request) => (
              <li
                key={request.pairing_id}
                className="rounded-card border border-warning/40 bg-warning-soft/50 p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 space-y-1.5">
                    <p className="flex items-center gap-2 font-medium text-foreground">
                      <ShieldAlert className="h-4 w-4 shrink-0 text-warning-text" aria-hidden />
                      New device requesting access
                    </p>
                    <p className="font-mono text-caption text-muted">
                      {request.device_hostname ?? 'unknown host'} · {request.device_os ?? 'unknown OS'}
                    </p>
                    <p className="font-mono text-caption text-muted">
                      {request.device_ip} · {request.device_region ?? 'unknown region'}
                    </p>
                    <p className="text-caption text-subtle">
                      Requested {new Date(request.requested_at).toLocaleString()} · expires{' '}
                      {new Date(request.expires_at).toLocaleTimeString()}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-warning-text">
                      Verification code
                    </p>
                    <p className="mt-1 rounded-control border border-warning/40 bg-surface px-3 py-1.5 font-mono text-base font-bold tracking-widest text-foreground">
                      {request.verification_code}
                    </p>
                    <p className="mt-1 text-[11px] text-muted">must match the agent's screen</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    loading={reject.isPending && reject.variables === request.pairing_id}
                    onClick={() => reject.mutate(request.pairing_id)}
                    leftIcon={<X className="h-3.5 w-3.5" />}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setApproveTarget(request)}
                    leftIcon={<Check className="h-3.5 w-3.5" />}
                  >
                    Approve…
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <Callout tone="success" title="No pending requests" icon={<Check className="h-4 w-4" />}>
          New pairing requests appear here the moment they arrive.
        </Callout>
      )}

      {/* ---------------- active ---------------- */}
      <section className="space-y-3" aria-label="Active devices">
        <h2 className="text-caption font-semibold uppercase tracking-widest text-subtle">
          Active devices ({active.length})
        </h2>
        {active.length === 0 ? (
          <EmptyState
            icon={<Monitor />}
            title="No devices connected"
            description="Install the agent on your machine, run it with an API key, and approve it here."
          />
        ) : (
          <DataTable
            data={active}
            columns={activeColumns}
            getRowId={(d) => d.device_id}
            cardTitle={(d) => d.nickname ?? d.device_hostname ?? 'Device'}
          />
        )}
      </section>

      {/* ---------------- revoked ---------------- */}
      {revoked.length > 0 ? (
        <section aria-label="Revoked devices">
          <Accordion
            items={[
              {
                value: 'revoked',
                trigger: `Revoked history (${revoked.length})`,
                content: (
                  <ul className="space-y-2">
                    {revoked.map((device) => (
                      <li
                        key={device.device_id}
                        className="flex flex-wrap items-center justify-between gap-2 text-caption"
                      >
                        <span className="text-foreground">
                          {device.nickname ?? device.device_hostname ?? device.device_fingerprint.slice(0, 12)}
                        </span>
                        <span className="text-subtle">
                          revoked {device.revoked_at ? new Date(device.revoked_at).toLocaleString() : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ),
              },
            ]}
          />
        </section>
      ) : null}

      {/* Approve modal */}
      <Modal
        open={approveTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setApproveTarget(null)
            setNickname('')
          }
        }}
        title="Approve this device"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setApproveTarget(null)}>
              Cancel
            </Button>
            <Button
              loading={approve.isPending}
              onClick={() => approveTarget && approve.mutate(approveTarget)}
            >
              Approve device
            </Button>
          </>
        }
      >
        {approveTarget ? (
          <div className="space-y-4">
            <Callout tone="warning" title="Confirm the code matches">
              The agent is displaying <strong>{approveTarget.verification_code}</strong> on its
              screen. If it differs, do not approve — someone else may hold your key.
            </Callout>

            <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-sunken p-3 text-caption">
              <div>
                <dt className="text-subtle">Hostname</dt>
                <dd className="font-mono text-foreground">
                  {approveTarget.device_hostname ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-subtle">IP</dt>
                <dd className="font-mono text-foreground">{approveTarget.device_ip}</dd>
              </div>
              <div>
                <dt className="text-subtle">Region</dt>
                <dd className="text-foreground">{approveTarget.device_region ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-subtle">OS</dt>
                <dd className="text-foreground">{approveTarget.device_os ?? '—'}</dd>
              </div>
            </dl>

            <Field label="Nickname" hint="Optional — helps you recognise it later.">
              <Input
                value={nickname}
                onChange={(event) => setNickname(event.currentTarget.value)}
                placeholder="prod-server-01"
              />
            </Field>
          </div>
        ) : null}
      </Modal>

      {/* Revoke */}
      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null)
        }}
        title={`Revoke "${revokeTarget?.nickname ?? revokeTarget?.device_hostname ?? 'this device'}"?`}
        description="Its session ends immediately and it must be re-paired to return."
        confirmLabel="Revoke device"
        destructive
        loading={revoke.isPending}
        onConfirm={() => revokeTarget && revoke.mutate(revokeTarget.device_id)}
      />
    </div>
  )
}
